import React from 'react';
import { Star, LayoutGrid, Maximize, Home, Eye, EyeOff } from 'lucide-react';
import { getSizeClass } from '../../config/constants';

export const FavoritesScreen = ({
    isDarkMode,
    favZoom,
    handleFavZoomChange,
    isDefaultFavorites,
    setIsDefaultFavorites,
    currentUser,
    showHiddenFavorites,
    setShowHiddenFavorites,
    displayFavorites,
    hubHiddenFavorites,
    handleHideFavoriteFromHub,
    handleCycleCardSize,
    handleOpenModule,
    setDraggedItem,
    handleDrop
}) => {
    return (
        <div className="flex-1 overflow-y-auto p-6 md:p-8 animate-fade-in bg-slate-50 dark:bg-slate-950">
            <div className="max-w-7xl mx-auto">
                <div className="mb-8 pb-4 border-b border-slate-300 dark:border-slate-800 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-slate-800 dark:text-white flex items-center gap-3">
                            <Star className="text-yellow-400 fill-yellow-400" /> Meus Favoritos
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">Todos os seus módulos em um só lugar.</p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3">
                        {/* SELETOR DE ZOOM */}
                        <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 shadow-sm border transition-colors ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-gray-200'}`}>
                            <div className="bg-blue-600 rounded-full p-1 flex items-center justify-center shrink-0" title="Diminuir">
                                <LayoutGrid size={10} className="text-white" />
                            </div>
                            <input 
                                type="range" min="1" max="10" 
                                value={favZoom} 
                                onChange={handleFavZoomChange}
                                className={`w-20 md:w-24 h-1.5 rounded-lg appearance-none cursor-pointer accent-blue-600 transition-colors ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}
                                title={`Zoom: ${favZoom}`}
                            />
                            <Maximize size={10} className={isDarkMode ? 'text-slate-300' : 'text-slate-500'} title="Aumentar" />
                        </div>

                        {/* BOTÃO DE DEFINIR TELA INICIAL */}
                        <button 
                            onClick={() => {
                                const newVal = !isDefaultFavorites;
                                setIsDefaultFavorites(newVal);
                                localStorage.setItem(`apex_default_screen_${currentUser?.id}`, newVal ? 'favorites' : 'hub');
                            }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm ${isDefaultFavorites ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border border-yellow-200' : 'bg-white border text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300'}`}
                            title="Definir como Tela Inicial ao fazer Login"
                        >
                            <Home size={16} />
                            {isDefaultFavorites ? 'Favoritos é a Tela Inicial' : 'Tornar Tela Inicial'}
                        </button>

                        {/* BOTÃO DE VER ARQUIVADOS (OLHO) */}
                        <button 
                            onClick={() => setShowHiddenFavorites(!showHiddenFavorites)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm ${showHiddenFavorites ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-white border text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300'}`}
                        >
                            {showHiddenFavorites ? <Eye size={16} /> : <EyeOff size={16} />}
                            {showHiddenFavorites ? 'Ocultar Arquivados' : 'Ver Arquivados'}
                        </button>
                    </div>
                </div>

                {displayFavorites.length === 0 ? (
                    <div className="text-center py-20 bg-white dark:bg-slate-900/50 rounded-3xl border border-dashed border-slate-300 dark:border-slate-800">
                        <Star size={48} className="mx-auto text-slate-200 mb-4" />
                        <h3 className="text-lg font-bold opacity-40">Nenhum favorito encontrado.</h3>
                    </div>
                ) : (
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                        
                        {/* GRADE DINÂMICA COM ZOOM (FAVORITOS) */}
                        <div className="flex-1 w-full grid gap-4 grid-flow-dense" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${80 + (favZoom * 15)}px, 1fr))`, gridAutoRows: `${80 + (favZoom * 15)}px` }}>
                            {displayFavorites.filter(m => m.moduleType !== 'externo').map((mod, index) => {
                                const isHidden = hubHiddenFavorites.includes(mod.id);
                                const sizeClass = getSizeClass(mod.size);
                                return (
                                    <div 
                                        key={mod.id || index}
                                        onClick={() => !isHidden && handleOpenModule(mod)}
                                        draggable={!isHidden}
                                        onDragStart={(e) => { if(!isHidden){ e.dataTransfer.setData('text/plain', mod.id); setDraggedItem(mod); } }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => handleDrop(e, mod, true)}
                                        className={`${sizeClass} rounded-3xl shadow-lg cursor-pointer transition-all flex flex-col items-center justify-center gap-2 bg-gradient-to-br ${mod.color} text-white relative overflow-hidden group ${isHidden ? 'opacity-50 grayscale hover:grayscale-0' : 'hover:-translate-y-1 hover:shadow-2xl'}`}
                                    >
                                        <button 
                                            onClick={(e) => handleHideFavoriteFromHub(e, mod.id)}
                                            className="absolute top-3 left-3 p-2 bg-black/10 hover:bg-black/30 rounded-full transition-all z-10"
                                            title={isHidden ? "Restaurar Favorito" : "Arquivar do Hub"}
                                        >
                                            {isHidden ? <Eye size={14} className="text-white" /> : <EyeOff size={14} className="text-white/80" />}
                                        </button>

                                        <button 
                                            onClick={(e) => handleCycleCardSize(e, mod, true)}
                                            className="absolute top-3 right-3 p-2 bg-black/10 hover:bg-black/30 rounded-full transition-all backdrop-blur-sm z-10 opacity-0 group-hover:opacity-100"
                                            title="Alterar Formato do Card"
                                        >
                                            <Maximize size={16} className="text-white drop-shadow-md" />
                                        </button>

                                        {React.createElement(mod.icon || LayoutGrid, { 
                                            size: sizeClass.includes('row-span-2') ? 48 : 36, 
                                            className: "drop-shadow-md mb-1" 
                                        })}
                                        
                                        <span className={`font-bold text-center px-4 leading-tight drop-shadow-md ${sizeClass.includes('col-span-2') ? 'text-sm' : 'text-[11px] md:text-xs'}`}>
                                            {mod.title}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* QUADRO DIREITO: ACESSOS EXTERNOS (FAVORITOS) */}
                        <div className={`w-full md:w-64 shrink-0 rounded-2xl shadow-sm border p-4 flex flex-col gap-3 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                            <h3 className={`text-sm font-black uppercase tracking-wider mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Acessos Externos</h3>
                            
                            {displayFavorites.filter(m => m.moduleType === 'externo' && (!hubHiddenFavorites.includes(m.id) || showHiddenFavorites)).map((mod, index) => {
                                const isHidden = hubHiddenFavorites.includes(mod.id);
                                return (
                                    <div key={mod.id || index} className={`group relative flex items-center justify-between p-2 rounded-xl border transition-all cursor-pointer shadow-sm ${isHidden ? 'opacity-50 grayscale' : 'hover:-translate-y-0.5'} ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-500' : 'bg-slate-50 border-gray-200 hover:border-gray-400'}`} onClick={() => !isHidden && handleOpenModule(mod)}>
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            {mod.customIconUrl ? (
                                                <img src={mod.customIconUrl} alt="icon" className="w-8 h-8 rounded-lg object-cover shrink-0 shadow-sm border border-slate-200 dark:border-slate-700 bg-white" referrerPolicy="no-referrer" />
                                            ) : (
                                                <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center bg-gradient-to-br ${mod.color || 'from-slate-500 to-slate-600'} text-white shadow-sm`}>
                                                    {React.createElement(mod.icon || LayoutGrid, { size: 14 })}
                                                </div>
                                            )}
                                            <span className="font-bold text-xs truncate pr-2">{mod.title}</span>
                                        </div>
                                        <button onClick={(e) => handleHideFavoriteFromHub(e, mod.id)} className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors z-10 shrink-0" title={isHidden ? "Restaurar" : "Arquivar"}>
                                            {isHidden ? <Eye size={14} className={isDarkMode ? 'text-slate-400' : 'text-gray-500'} /> : <EyeOff size={14} className={isDarkMode ? 'text-slate-500' : 'text-gray-400'} />}
                                        </button>
                                    </div>
                                );
                            })}
                            {displayFavorites.filter(m => m.moduleType === 'externo' && (!hubHiddenFavorites.includes(m.id) || showHiddenFavorites)).length === 0 && <p className="text-xs opacity-50 text-center py-4">Nenhum atalho salvo.</p>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FavoritesScreen;
